import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
  useMediaQuery
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { useAuth } from '../hooks/useAuth';
import sentinelLogoWithTitle from '../../assets/icons/sentinel-logo-blue.svg';
import loginBackground2 from '../assets/images/login-background-2.png';
import spinnakerLogo from '../assets/images/spinnaker-logo.png';

import AuthLayout from './shared/AuthLayout';
import AuthHeader from './shared/AuthHeader';
import AuthCard from './shared/AuthCard';
import { AUTH_COLORS, AUTH_BREAKPOINTS } from './shared/authConstants';

const SentinelAuth = ({ onBack }) => {
  const [formType, setFormType] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const isMobile = useMediaQuery(AUTH_BREAKPOINTS.mobile);

  const getMobileFieldSx = () => ({
    '& .MuiOutlinedInput-root': {
      fontSize: isMobile ? '14px' : '16px',
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23)'
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.87)'
      },
      '&.Mui-focused fieldset': {
        borderColor: AUTH_COLORS.PRIMARY_BLUE
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: isMobile ? '14px' : '16px'
    }
  });

  const {
    formData,
    formErrors,
    handleChange,
    validateForm,
    handleSubmit: submitForm
  } = useAuth(formType);

  const handleSubmit = async e => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await submitForm(formData, formType);
        alert(`${formType} successful! (This is a demo)`);
      } catch (error) {
        alert(`${formType} failed: ${error.message}`);
      }
    }
  };

  const renderContent = () => (
    <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <AuthHeader
        icon={sentinelLogoWithTitle}
        title=''
        subtitle={formType === 'login' ? 'Welcome Back' : 'Reset Password'}
        description={
          formType === 'login'
            ? 'Your Risk Management App'
            : 'Enter your email to reset your password'
        }
        logoSrc={spinnakerLogo}
      />
      {renderAuthFields()}
      <Button
        type='submit'
        fullWidth
        variant='contained'
        sx={{
          mt: 2,
          mb: 2,
          backgroundColor: AUTH_COLORS.PRIMARY_BLUE,
          '&:hover': { backgroundColor: AUTH_COLORS.PRIMARY_BLUE_HOVER },
          py: isMobile ? 1.2 : 1.5,
          fontSize: isMobile ? '0.875rem' : '1rem',
          fontWeight: 600,
          textTransform: 'none'
        }}
      >
        {formType === 'login' ? 'Sign In' : 'Send Reset Link'}
      </Button>{' '}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        {formType === 'login' ? (
          <Button
            variant='text'
            onClick={() => setFormType('forgot')}
            sx={{
              color: AUTH_COLORS.PRIMARY_BLUE,
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              textTransform: 'none'
            }}
          >
            Forgot Password?
          </Button>
        ) : (
          <Button
            variant='text'
            onClick={() => setFormType('login')}
            sx={{
              color: AUTH_COLORS.PRIMARY_BLUE,
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              textTransform: 'none'
            }}
          >
            Back to Sign In
          </Button>
        )}
      </Box>
    </Box>
  );

  const renderAuthFields = () => (
    <>
      <TextField
        fullWidth
        label='Email'
        name='email'
        type='email'
        value={formData.email}
        onChange={handleChange}
        error={!!formErrors.email}
        helperText={formErrors.email}
        variant='outlined'
        size={isMobile ? 'small' : 'medium'}
        sx={{ mb: isMobile ? 1.5 : 2, ...getMobileFieldSx() }}
      />

      {formType === 'login' && (
        <FormControl
          fullWidth
          variant='outlined'
          sx={{ mb: isMobile ? 1.5 : 2 }}
        >
          <InputLabel
            htmlFor='outlined-adornment-password'
            size={isMobile ? 'small' : 'medium'}
          >
            Password
          </InputLabel>
          <OutlinedInput
            id='outlined-adornment-password'
            type={showPassword ? 'text' : 'password'}
            name='password'
            value={formData.password}
            onChange={handleChange}
            error={!!formErrors.password}
            size={isMobile ? 'small' : 'medium'}
            endAdornment={
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={() => setShowPassword(!showPassword)}
                  edge='end'
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label='Password'
            sx={getMobileFieldSx()}
          />
          {formErrors.password && (
            <FormHelperText error>{formErrors.password}</FormHelperText>
          )}
        </FormControl>
      )}
    </>
  );

  return (
    <AuthLayout onBack={onBack} backgroundImage={loginBackground2}>
      <AuthCard>{renderContent()}</AuthCard>
    </AuthLayout>
  );
};

export default SentinelAuth;
